import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTutorial, useTutorialAutoStart, useTutorialData } from '@nsi-advisory-services/components';
import { addDays, formatDate, formatHM, isWeekend, listDays } from '../utils/timesheet';

export const TIMESHEET_TUTORIAL = 'timesheet';

// A day's worth of work that owes a meal break under the default rule.
const MEAL_BREAK_SECONDS = 5 * 3600;
const BREAK_ROLES = new Set(['meal_break', 'paid_break']);
const shortDate = (iso) => formatDate(iso, { weekday: 'long', month: 'short', day: 'numeric' });

// The entry the person makes in the first section, which the rest of the tutorial works on.
const mainEntry = ({ data, state }) => (data.entries || []).find((e) => e.id === state.mainId) || null;
const practiceEntries = ({ data, state }) => (data.entries || []).filter((e) => (state.practiceIds || []).includes(e.id));
const copyOfMain = (ctx) => {
  const main = mainEntry(ctx);
  if (!main) return null;
  return practiceEntries(ctx).find((e) => e.id !== main.id && e.taskId === main.taskId && e.date === main.date) || null;
};
const breakEntry = (ctx) => {
  const byId = new Map((ctx.data.tasks || []).map((t) => [t.id, t]));
  return practiceEntries(ctx).find((e) => BREAK_ROLES.has(byId.get(e.taskId)?.systemRole)) || null;
};

const row = (id) => `.ts-agenda__row[data-entry-id="${id}"]`;
const inRow = (entry, selector) => (entry ? `${row(entry.id)} ${selector}` : null);
const inMain = (selector) => (ctx) => inRow(mainEntry(ctx), selector);
const card = (ctx) => (mainEntry(ctx) ? `.ts-week__item[data-entry-id="${mainEntry(ctx).id}"]` : null);

const canLog = (ctx) => Boolean(ctx.data.editable && !ctx.data.timerRunning && ctx.data.tasks?.length);
const hasMain = (ctx) => Boolean(mainEntry(ctx));
const canDrag = () => !window.matchMedia?.('(pointer: coarse)').matches;
const ADD_EVENT = 'button[aria-label^="Add entry"]';

// For a step about something the step before opened: a menu, a dialog. Going back passes over it,
// and so does skipping the step that would have opened it.
const ON_WHAT_OPENED = { skipScroll: true, transient: true, ifMissing: 'skip', waitFor: 800 };

// An empty weekday to practice on, so nothing here lands among real entries: today if it's free,
// otherwise the nearest free day in the pay period, or the next period if this one is locked.
const pickPracticeDate = ({ today, period, entries, editable }) => {
  if (!period) return today;
  if (!editable) return listDays(addDays(period.end, 1), addDays(period.end, 5)).find((d) => !isWeekend(d));
  const free = (d) => !isWeekend(d) && !entries.some((e) => e.date === d);
  const days = listDays(period.start, period.end);
  const ahead = days.filter((d) => d >= today);
  const behind = days.filter((d) => d < today).reverse();
  return [...ahead, ...behind].find(free) || today;
};

// Puts the page where a step needs it, so going back never lands on the wrong view.
const show = (view, date = null) => (ctx) => {
  const target = (date ? date(ctx) : null) || ctx.data.date;
  if (ctx.data.view !== view || ctx.data.date !== target) ctx.data.show?.({ view, date: target });
};
const onPracticeDay = (ctx) => mainEntry(ctx)?.date || ctx.state.practiceDate;

const timesheetTutorial = {
  id: TIMESHEET_TUTORIAL,
  title: 'Timesheet tutorial',
  version: 1,
  path: '/timesheet',
  steps: [
    {
      id: 'welcome',
      section: 'Welcome',
      title: 'Welcome to your new timesheet',
      onEnter: (ctx) => {
        const practiceDate = ctx.state.practiceDate || pickPracticeDate(ctx.data);
        if (!ctx.state.practiceDate) ctx.setState({ practiceDate, practiceIds: [] });
        ctx.data.show?.({ view: 'day', date: practiceDate });
      },
      content: (ctx) => (
        <>
          <p>In a few minutes you&rsquo;ll log time, change it, and see every way of viewing it. You&rsquo;ll be doing it for real, on the actual page.</p>
          <p>
            We&rsquo;ll practice on <strong>{ctx.state.practiceDate ? shortDate(ctx.state.practiceDate) : 'an open day'}</strong>.
            Anything you add is yours to remove with one click at the end, and nothing goes anywhere until you submit a pay period.
          </p>
          <p>Close this any time and pick it back up from the <strong>Help</strong> button.</p>
        </>
      ),
    },
    {
      id: 'range',
      section: 'Finding your way',
      title: 'How you’re looking, and where',
      target: '.ts-rangebar',
      placement: 'bottom',
      onEnter: show('day', onPracticeDay),
      content: (
        <p>Switch between <strong>Day</strong>, <strong>Week</strong> and <strong>Pay period</strong> here, and step through time with the arrows.</p>
      ),
    },

    {
      id: 'log-card',
      section: 'Log time',
      title: 'Logging an entry',
      target: '.ts-logcard',
      placement: 'left',
      checkpoint: true,
      when: canLog,
      onEnter: show('day', onPracticeDay),
      content: <p>This card is where most of your time goes in: pick what you worked on, say what you did, and when. Let&rsquo;s make one.</p>,
    },
    {
      id: 'log-task-open',
      section: 'Log time',
      title: 'Choose a project and task',
      target: '[data-tutorial="log-task"]',
      placement: 'left',
      when: canLog,
      interactive: true,
      action: {
        hint: 'Click the Project / task box.',
        done: (ctx) => Boolean(ctx.query('.ts-tp-popup')) || Boolean(ctx.query('[data-tutorial="log-task"][data-has-value="true"]')),
      },
      content: <p>Every task you can log to lives in this list.</p>,
    },
    {
      id: 'log-task-choose',
      section: 'Log time',
      title: 'Pick any task',
      target: '.ts-tp-popup',
      spotlight: '[data-tutorial="log-task"]',
      placement: 'left',
      ...ON_WHAT_OPENED,
      when: canLog,
      lostTo: 'log-task-open',
      action: {
        hint: 'Search or open a project, then click a task.',
        done: (ctx) => Boolean(ctx.query('[data-tutorial="log-task"][data-has-value="true"]')),
      },
      content: <p>Type to search, or open a project with its arrow. The star pins a task to your <strong>Favorites</strong> at the top. Any task will do for practice.</p>,
    },
    {
      id: 'log-note',
      section: 'Log time',
      title: 'Say what you did',
      target: '[data-tutorial="log-note"]',
      placement: 'left',
      when: canLog,
      action: {
        hint: 'Type a short note, like “Practicing”, then press Next.',
        advance: false,
        done: (ctx) => Boolean(ctx.query('[data-tutorial="log-note"] input')?.value.trim()),
      },
      content: <p>This is what your reviewer and the reports see.</p>,
    },
    {
      id: 'log-times',
      section: 'Log time',
      title: 'Say when',
      target: '[data-tutorial="log-times"]',
      placement: 'left',
      when: canLog,
      action: {
        hint: 'Set a start and end, like 9:00 AM to 10:00 AM, then press Next.',
        advance: false,
        done: (ctx) => Boolean(ctx.query('[data-tutorial="log-times"][data-has-length="true"]')),
      },
      content: <p>Click a time and choose from the panel, or type it. Prefer hours? Set the <strong>Length</strong> instead; the end time follows.</p>,
    },
    {
      id: 'log-add',
      section: 'Log time',
      title: 'Add it',
      target: '[data-tutorial="log-add"]',
      placement: 'left',
      when: canLog,
      action: { hint: 'Click Add entry.', done: hasMain },
      content: <p>Your entry goes on the day. The next one starts where this one ended.</p>,
    },

    {
      id: 'edit-row',
      section: 'Edit and delete',
      title: 'There it is',
      target: (ctx) => (mainEntry(ctx) ? row(mainEntry(ctx).id) : null),
      placement: 'bottom',
      checkpoint: true,
      when: hasMain,
      counts: canLog,
      onEnter: show('day', onPracticeDay),
      content: <p>Everything on a row can be changed right where it is: click the task, the note, either time or the length. It saves when you click away.</p>,
    },
    {
      id: 'edit-note',
      section: 'Edit and delete',
      title: 'Change the note',
      target: inMain('.ts-agenda__note'),
      placement: 'bottom',
      when: hasMain,
      counts: canLog,
      onEnter: (ctx) => {
        show('day', onPracticeDay)(ctx);
        if (ctx.direction > 0) ctx.setState({ noteBefore: mainEntry(ctx)?.description || '' });
      },
      action: {
        hint: 'Click the note, change it, then click away or press Enter.',
        done: (ctx) => (mainEntry(ctx)?.description || '') !== (ctx.state.noteBefore || ''),
      },
      content: <p>Same for anything else on the row.</p>,
    },
    {
      id: 'edit-length',
      section: 'Edit and delete',
      title: 'Change the length',
      target: inMain('.ts-agenda__length'),
      placement: 'left',
      when: hasMain,
      counts: canLog,
      onEnter: show('day', onPracticeDay),
      action: {
        hint: (ctx) => `Click ${formatHM(mainEntry(ctx)?.durationSeconds || 0)}, type 6, then press Enter.`,
        done: (ctx) => (mainEntry(ctx)?.durationSeconds || 0) >= MEAL_BREAK_SECONDS,
      },
      content: <p>The end time moves to fit. Six hours also sets up what&rsquo;s next.</p>,
    },

    {
      id: 'meal-break',
      section: 'Meal breaks',
      title: 'Add a meal break in one click',
      target: '[data-tutorial="fix-add_breaks"]',
      spotlight: '.ts-checks',
      placement: 'top',
      waitFor: 8000,
      when: hasMain,
      counts: canLog,
      onEnter: show('day', onPracticeDay),
      action: { hint: 'Click Add meal break.', done: (ctx) => Boolean(breakEntry(ctx)) },
      content: (ctx) => (ctx.targetMissing ? (
        <p>When a day owes a meal break, a banner appears under its entries with a button that adds it for you, right where it falls due. This day doesn&rsquo;t owe one under your rules, so there&rsquo;s nothing to add here.</p>
      ) : (
        <p>The timesheet runs the same checks as View Hours while you work. Six hours owes a meal break, so it flagged the day and offered to fix it. The break goes in where it falls due, and other checks with a fix work the same way.</p>
      )),
    },
    {
      id: 'duplicate',
      section: 'Edit and delete',
      title: 'Duplicate an entry',
      target: inMain('[aria-label="Duplicate"]'),
      placement: 'left',
      padding: 4,
      when: hasMain,
      counts: canLog,
      onEnter: show('day', onPracticeDay),
      action: { hint: 'Click the copy icon.', done: (ctx) => Boolean(copyOfMain(ctx)) },
      content: <p>Same task, note and length, starting where the day&rsquo;s last entry ended. These icons show when you hover over a row.</p>,
    },
    {
      id: 'delete-open',
      section: 'Edit and delete',
      title: 'Delete the copy',
      target: (ctx) => inRow(copyOfMain(ctx), '[aria-label="Delete"]'),
      placement: 'left',
      padding: 4,
      when: (ctx) => Boolean(copyOfMain(ctx)),
      counts: canLog,
      onEnter: show('day', onPracticeDay),
      action: {
        hint: 'Click the trash icon on the copy.',
        done: (ctx) => !copyOfMain(ctx) || Boolean(ctx.query('.nsi-modal__dialog')),
      },
      content: <p>Deleting always asks first.</p>,
    },
    {
      id: 'delete-confirm',
      section: 'Edit and delete',
      title: 'Confirm it',
      target: '.nsi-modal__dialog',
      placement: 'bottom',
      ...ON_WHAT_OPENED,
      lostTo: 'delete-open',
      when: (ctx) => Boolean(copyOfMain(ctx)),
      counts: canLog,
      action: { hint: 'Click Delete.', done: (ctx) => !copyOfMain(ctx) },
      content: <p>A deleted entry can&rsquo;t be brought back, though admins can still see that it existed in its history.</p>,
    },

    {
      id: 'week-open',
      section: 'Week view',
      title: 'The week view',
      target: '[data-tutorial="view-week"]',
      placement: 'bottom',
      checkpoint: true,
      interactive: true,
      onEnter: (ctx) => {
        if (ctx.direction < 0) show('day', onPracticeDay)(ctx);
      },
      action: { hint: 'Click Week.', done: (ctx) => ctx.data.view === 'week' },
      content: <p>See the whole week at a glance, with a column per day.</p>,
    },
    {
      id: 'week-columns',
      section: 'Week view',
      title: 'A column per day',
      target: '.ts-week',
      placement: 'top',
      onEnter: show('week', onPracticeDay),
      content: <p>Each day shows its total and a bar toward a full day, then a card per entry. A day flags itself if a check fails or nothing was logged. Click a day&rsquo;s heading to open it, or <strong>Log time</strong> to add to it.</p>,
    },
    {
      id: 'week-edit-open',
      section: 'Week view',
      title: 'Edit from the week',
      target: card,
      placement: 'bottom',
      when: hasMain,
      counts: canLog,
      onEnter: show('week', onPracticeDay),
      action: { hint: 'Click your entry’s card, change something like the note, then click Save.', done: (ctx) => Boolean(ctx.query('.nsi-modal__dialog')) },
      content: <p>A card opens its entry, with everything about it in one place, including its date. We&rsquo;ll carry on once you close it.</p>,
    },
    {
      id: 'week-drag',
      section: 'Week view',
      title: 'Drag it to another day',
      // Not until the dialog from the step before has been closed, however long that takes.
      target: (ctx) => (ctx.query('.nsi-modal__dialog') ? null : '.ts-week'),
      hideWhileWaiting: true,
      waitFor: 30 * 60 * 1000,
      placement: 'top',
      when: (ctx) => hasMain(ctx) && canDrag(),
      counts: (ctx) => canLog(ctx) && canDrag(),
      onEnter: (ctx) => {
        show('week', onPracticeDay)(ctx);
        if (ctx.direction > 0) ctx.setState({ dragFrom: mainEntry(ctx)?.date });
      },
      action: {
        hint: 'Drag your card onto another day.',
        done: (ctx) => Boolean(ctx.state.dragFrom) && mainEntry(ctx)?.date !== ctx.state.dragFrom,
      },
      content: <p>It keeps its times. If something&rsquo;s already there and overlaps aren&rsquo;t allowed, it goes to the nearest open spot that day.</p>,
    },
    {
      id: 'week-menu',
      section: 'Week view',
      title: 'Or right-click a card',
      target: card,
      // To the side, since the menu opens under the pointer and would end up beneath the card.
      placement: 'left',
      interactive: true,
      when: hasMain,
      counts: canLog,
      onEnter: show('week', onPracticeDay),
      content: <p>Right-click any card for <strong>Edit</strong>, <strong>Move</strong>, <strong>Clone</strong> and <strong>Delete</strong>. Try it on this one if you like.</p>,
    },

    {
      id: 'period-open',
      section: 'Pay period',
      title: 'The pay period view',
      target: '[data-tutorial="view-period"]',
      placement: 'bottom',
      checkpoint: true,
      interactive: true,
      onEnter: (ctx) => {
        if (ctx.direction < 0) show('week', onPracticeDay)(ctx);
      },
      action: { hint: 'Click Pay period.', done: (ctx) => ctx.data.view === 'period' },
      content: <p>The whole pay period, which is what gets submitted and approved.</p>,
    },
    {
      id: 'period-grid',
      section: 'Pay period',
      title: 'Task by day',
      target: '.ts-grid',
      placement: 'top',
      onEnter: show('period', onPracticeDay),
      content: (
        <>
          <p>A row per task and a column per day, darker the more hours a cell has. Check a period at a glance.</p>
          <ul>
            <li>A <strong>filled cell</strong> opens its entry, or the day if it has several.</li>
            <li>An <strong>empty cell</strong> adds time to that task on that day.</li>
            <li>A <strong>day heading</strong> opens that day.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'period-submit',
      section: 'Pay period',
      title: 'Submitting',
      target: '.ts-rangebar__tools',
      placement: 'bottom',
      onEnter: show('period', onPracticeDay),
      content: <p>When the period is finished, <strong>Submit for approval</strong> sends it to your reviewer and locks its time. Not today, though: we&rsquo;re just looking.</p>,
    },

    {
      id: 'calendar-list',
      section: 'Outlook calendar',
      title: 'Your Outlook calendar',
      target: '.ts-calendar',
      placement: 'top',
      checkpoint: true,
      waitFor: 2500,
      when: (ctx) => Boolean(ctx.data.canUseCalendar),
      // Today, where there are likely to be real events.
      onEnter: show('day', (ctx) => ctx.data.today),
      content: (ctx) => (ctx.targetMissing ? (
        <p>On any day you can log to, your Outlook events show here under your entries, each with a button that turns it into an entry.</p>
      ) : (
        <p>Back on the day view, your meetings from Outlook show here, ready to become entries without retyping them. <strong>Refresh calendar</strong> pulls in anything that just changed.</p>
      )),
    },
    {
      id: 'calendar-add',
      section: 'Outlook calendar',
      title: 'Add one as an entry',
      target: `.ts-calendar__row ${ADD_EVENT}`,
      spotlight: (ctx) => ctx.query(`.ts-calendar__row ${ADD_EVENT}`)?.closest('.ts-calendar__row'),
      placement: 'top',
      waitFor: 2500,
      ifMissing: 'skip',
      when: (ctx) => Boolean(ctx.data.canUseCalendar),
      onEnter: show('day', (ctx) => ctx.data.today),
      action: { hint: 'Click Add entry on an event.', done: (ctx) => Boolean(ctx.query('.ts-quick')) },
      content: <p>The times and the note come from the event.</p>,
    },
    {
      id: 'calendar-quick',
      section: 'Outlook calendar',
      title: 'Just pick the task',
      target: '.ts-quick',
      placement: 'left',
      ...ON_WHAT_OPENED,
      lostTo: 'calendar-add',
      when: (ctx) => Boolean(ctx.data.canUseCalendar),
      onEnter: (ctx) => {
        if (ctx.direction > 0) ctx.setState({ countBeforeCalendar: (ctx.state.practiceIds || []).length });
      },
      action: {
        hint: 'Choose a task, then click Add.',
        done: (ctx) => (ctx.state.practiceIds || []).length > (ctx.state.countBeforeCalendar ?? Infinity),
      },
      content: <p>Everything else is filled in. Once it&rsquo;s logged, the event shows <strong>Added</strong> so you don&rsquo;t log it twice.</p>,
    },

    {
      id: 'tutorial-button',
      section: 'All done',
      title: 'Come back any time',
      target: '[data-tutorial-button="timesheet"]',
      placement: 'bottom',
      checkpoint: true,
      onEnter: show('day', (ctx) => ctx.data.today),
      content: <p>This button runs the tutorial again whenever you want a refresher.</p>,
    },
    {
      id: 'finish',
      section: 'All done',
      title: 'You’re ready',
      content: <p>That&rsquo;s logging, editing, the week and pay period views, meal breaks and your calendar. Next you can clear away the practice entries or keep them.</p>,
    },
  ],

  // The practice entries are real entries, so they don't outlive the tutorial unless asked to.
  onFinish: async (ctx) => {
    const ids = ctx.state.practiceIds || [];
    if (ids.length) {
      const count = ids.length === 1 ? 'a practice entry' : `${ids.length} practice entries`;
      const remove = await ctx.confirm({
        title: 'Remove your practice entries?',
        body: `The tutorial added ${count} to your timesheet. If you logged something you want to keep, like a real meeting, keep them and delete the rest yourself.`,
        confirmText: 'Remove them',
        cancelText: 'Keep them',
      });
      if (remove) {
        // One already deleted by hand just fails, which is fine.
        await Promise.all(ids.map((id) => ctx.data.deleteEntry?.(id).catch(() => {})));
      }
    }
    ctx.data.show?.({ view: 'day', date: ctx.data.today });
  },
};

export default timesheetTutorial;

/**
 * Connects the timesheet page to its tutorial: gives the steps the page's values (`page`), notes
 * every entry created while the tutorial runs, and starts it on someone's first visit. Not on a
 * phone, where the sidebar and drag steps don't fit; the button still runs it there.
 */
export function useTimesheetTutorial(page, { autoStart = true } = {}) {
  const { activeId, getState, setState } = useTutorial();
  const queryClient = useQueryClient();
  useTutorialData(page);
  useTutorialAutoStart(TIMESHEET_TUTORIAL, { enabled: autoStart && window.innerWidth >= 768 });

  useEffect(() => {
    if (activeId !== TIMESHEET_TUTORIAL) return undefined;
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action?.type !== 'success') return;
      const { mutation } = event;
      const key = mutation.options.mutationKey?.[0];
      const ids = getState().practiceIds || [];
      if (key === 'tsSaveEntry' && !mutation.state.variables?.id && mutation.state.data?.id) {
        const { id } = mutation.state.data;
        setState({ practiceIds: [...ids, id], mainId: getState().mainId || id });
      } else if (key === 'tsDeleteEntry') {
        setState({ practiceIds: ids.filter((id) => id !== mutation.state.variables) });
      }
    });
  }, [activeId, getState, queryClient, setState]);
}
